import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

import { EButtonStyleType } from '@common/enums';

import type { ThemeButton, ThemeButtonData } from '../../../types/theme-button.type';

import { BUTTON_STYLE_TYPE_TRANSLATIONS } from '../../../config/translation-keys.const';
import { cssSizeToString } from '../../../helpers/font-css';
import { themeColorCss } from '../../../utils/variables';

/** Which global class paints a selected preset in the live theme's colours. */
const SELECTED_CLASS: Partial<Record<EButtonStyleType, string>> = {
    [EButtonStyleType.primary]: 'sp-ui-button-primary-light',
    [EButtonStyleType.secondary]: 'sp-ui-button-secondary-light',
};

/**
 * One swatch in the button preset strip.
 *
 * **The selected swatch has no styles of its own.** It gets `sp-ui-button-{type}-light`, and that
 * global class already sets the six `--{type}-button-*` variables; `.sp-ui-button` already sets the
 * seven `--button-*` font ones. This component used to re-emit every one of those as an inline
 * `var(…)` through a JSS `getStyles()`, which is the same declarations written twice — once in the
 * stylesheet everyone else uses, once here where nobody could see them.
 *
 * What genuinely differs per swatch is the UNSELECTED case: it previews a preset the theme is not
 * using, so its colours come from that preset's own `base` rather than from any theme variable.
 * Those go out as custom properties and the `.less` reads them.
 */
@Component({
    selector: 'sp-theme-button-preset',
    templateUrl: './button-preset.component.html',
    styleUrls: ['./button-preset.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslateModule],
})
export class SpThemeButtonPresetComponent {
    public readonly button = input.required<ThemeButton>();
    public readonly selected = input(false);

    public readonly BUTTON_STYLE_TYPE_TRANSLATIONS = BUTTON_STYLE_TYPE_TRANSLATIONS;

    public readonly classes = computed(() => {
        const base = 'sp-ui-button sp-ui-button-sm';

        return this.selected() ? `${base} ${SELECTED_CLASS[this.button().styleType] ?? ''}` : `${base} is-preview`;
    });

    public readonly previewStyles = computed<Record<string, string>>(() => {
        const base: ThemeButtonData | undefined = this.button().base;

        // No rest state to preview. A button carrying only the two bags `base` superseded is left
        // exactly as the migration found it, and guessing a look for it here would show the user a
        // preset that does not exist.
        if (this.selected() || !base) {
            return {};
        }

        const { color, background, borderColor, borderRadius, borderStyle, borderWidth } = base;

        return {
            '--preset-button-color': themeColorCss(color[0]!),
            '--preset-button-background': themeColorCss(background[0]!),
            '--preset-button-border-color': themeColorCss(borderColor[0]!),
            '--preset-button-border-radius': cssSizeToString(borderRadius),
            '--preset-button-border-style': borderStyle,
            '--preset-button-border-width': cssSizeToString(borderWidth),
        };
    });
}
